import { forwardRef, memo, useState, type ReactNode } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar'
import { MessageContext } from '../../contexts'
import type { Feedback, IntegrationMessage, Renderers, RichBlockMessage } from '../../types'
import { Block } from '../Block'
import { hashStringToInt } from '../../utils'
import { webchatClasses } from '../../styles/classes'
import clsx from 'clsx'

type MessageProps = {
  children?: ReactNode
  renderers?: Partial<Renderers>
  sendMessage?: (payload: IntegrationMessage['payload']) => void
  addMessageFeedback?: (messageId: string, feedback: Feedback) => void
  isReadOnly?: boolean
  isLastDeliveredMessage?: boolean
  showAvatar?: boolean
} & RichBlockMessage

export const Message = memo(
  forwardRef<HTMLDivElement, MessageProps>(
    (
      {
        direction,
        block,
        disableInput,
        children,
        sender,
        id,
        authorId,
        status,
        renderers,
        isReadOnly,
        isLastDeliveredMessage,
        feedback,
        timestamp,
        metadata,
        sendMessage,
        addMessageFeedback,
        showAvatar,
      },
      ref
    ) => {
      const [isLoading, setIsLoading] = useState<string[]>([])
      const {
        message: { container, deliveryStatus },
      } = webchatClasses

      // Default behavior but this should be removed when we officially support this setting
      showAvatar = showAvatar ?? direction === 'incoming'

      return (
        <MessageContext.Provider value={{ isLoading, setIsLoading }}>
          <div
            {...container}
            className={clsx(container?.className, isLastDeliveredMessage && deliveryStatus?.className)}
            data-loaded={isLoading.length === 0}
            data-disable-input={!!disableInput}
            data-direction={direction}
            dir="auto"
            ref={ref}
          >
            {direction === 'incoming' && showAvatar && <MessageAvatar authorId={authorId} sender={sender} />}
            {block && (
              <Block
                block={{
                  ...block,
                  isReadOnly,
                  direction,
                  timestamp,
                  sender,
                  feedback,
                  messageId: id,
                  status,
                  metadata,
                  sendMessage,
                  addMessageFeedback,
                }}
                renderers={renderers}
              />
            )}
            {direction === 'outgoing' && showAvatar && <MessageAvatar authorId={authorId} sender={sender} />}
            {children}
          </div>
        </MessageContext.Provider>
      )
    }
  )
)

type MessageAvatarProps = {
  authorId: string | undefined
  sender: { avatar?: string; name: string }
}

const MessageAvatar = ({ authorId, sender }: MessageAvatarProps) => {
  const {
    message: { avatar },
  } = webchatClasses

  const [avatarStatus, setAvatarStatus] = useState<'loading' | 'loaded' | 'error' | 'idle'>('idle')

  const authorIdHash = hashStringToInt(authorId ?? '', 15)
  return (
    <Avatar data-color={authorIdHash} data-loading-status={avatarStatus} {...avatar?.container}>
      <AvatarImage {...avatar?.image} onLoadingStatusChange={setAvatarStatus} src={sender?.avatar} />
      <AvatarFallback {...avatar?.fallback}>{sender?.name[0]} </AvatarFallback>
    </Avatar>
  )
}
